/**
 * Performance Monitor Service - Tracks query performance and optimization metrics
 */

interface QueryMetric {
  queryType: string;
  duration: number;
  timestamp: number;
  cached: boolean;
  deduplicated: boolean;
  error?: string;
}

interface PerformanceStats {
  totalQueries: number;
  averageQueryTime: number;
  cacheHitRate: number;
  deduplicationRate: number;
  errorRate: number;
  slowQueries: QueryMetric[];
}

class PerformanceMonitorService {
  private metrics: QueryMetric[] = [];
  private readonly MAX_METRICS = 1000;
  private readonly SLOW_QUERY_THRESHOLD = 2000; // 2 seconds

  /**
   * Record a query metric
   */
  recordQuery(
    queryType: string,
    duration: number,
    options: {
      cached?: boolean;
      deduplicated?: boolean;
      error?: string;
    } = {}
  ): void {
    const metric: QueryMetric = {
      queryType,
      duration,
      timestamp: Date.now(),
      cached: options.cached || false,
      deduplicated: options.deduplicated || false,
      error: options.error
    };

    this.metrics.push(metric);

    // Keep only recent metrics
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics = this.metrics.slice(-this.MAX_METRICS);
    }

    // Log slow queries in development only
    if (import.meta.env.DEV && duration > this.SLOW_QUERY_THRESHOLD) {
      console.warn(`Slow query detected: ${queryType} (${duration}ms)`);
    }
  }

  /**
   * Get performance statistics
   */
  getStats(timeWindow?: number): PerformanceStats {
    const cutoff = timeWindow ? Date.now() - timeWindow : 0;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    if (relevantMetrics.length === 0) {
      return {
        totalQueries: 0,
        averageQueryTime: 0,
        cacheHitRate: 0,
        deduplicationRate: 0,
        errorRate: 0,
        slowQueries: []
      };
    }

    const totalQueries = relevantMetrics.length;
    const totalTime = relevantMetrics.reduce((sum, m) => sum + m.duration, 0);
    const cachedQueries = relevantMetrics.filter(m => m.cached).length;
    const deduplicatedQueries = relevantMetrics.filter(m => m.deduplicated).length;
    const errorQueries = relevantMetrics.filter(m => m.error).length;
    const slowQueries = relevantMetrics.filter(m => m.duration > this.SLOW_QUERY_THRESHOLD);

    return {
      totalQueries,
      averageQueryTime: totalTime / totalQueries,
      cacheHitRate: (cachedQueries / totalQueries) * 100,
      deduplicationRate: (deduplicatedQueries / totalQueries) * 100,
      errorRate: (errorQueries / totalQueries) * 100,
      slowQueries
    };
  }

  /**
   * Get query breakdown by type
   */
  getQueryBreakdown(timeWindow?: number): Record<string, number> {
    const cutoff = timeWindow ? Date.now() - timeWindow : 0;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    const breakdown: Record<string, number> = {};
    
    relevantMetrics.forEach(metric => {
      breakdown[metric.queryType] = (breakdown[metric.queryType] || 0) + 1;
    });

    return breakdown;
  }

  /**
   * Get optimization impact
   */
  getOptimizationImpact(timeWindow: number = 60 * 60 * 1000): {
    timesSaved: number;
    queriesOptimized: number;
    estimatedTimeSaved: number;
  } {
    const cutoff = Date.now() - timeWindow;
    const relevantMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    const optimizedQueries = relevantMetrics.filter(m => m.cached || m.deduplicated);
    const averageQueryTime = this.getStats(timeWindow).averageQueryTime;
    
    // Estimate time saved (assuming cache/dedup saves 80% of query time)
    const estimatedTimeSaved = optimizedQueries.length * averageQueryTime * 0.8;

    return {
      timesSaved: optimizedQueries.length,
      queriesOptimized: optimizedQueries.length,
      estimatedTimeSaved
    };
  }

  /**
   * Log performance summary
   */
  logPerformanceSummary(timeWindow: number = 60 * 60 * 1000): void {
    const stats = this.getStats(timeWindow);
    const breakdown = this.getQueryBreakdown(timeWindow);
    const impact = this.getOptimizationImpact(timeWindow);

    console.group('📊 PERFORMANCE SUMMARY (Last Hour)');
    console.log('Total Queries:', stats.totalQueries);
    console.log('Average Query Time:', Math.round(stats.averageQueryTime) + 'ms');
    console.log('Cache Hit Rate:', Math.round(stats.cacheHitRate) + '%');
    console.log('Deduplication Rate:', Math.round(stats.deduplicationRate) + '%');
    console.log('Error Rate:', Math.round(stats.errorRate) + '%');
    console.log('Slow Queries:', stats.slowQueries.length);
    console.log('Queries Optimized:', impact.queriesOptimized);
    console.log('Estimated Time Saved:', Math.round(impact.estimatedTimeSaved) + 'ms');
    console.log('Query Breakdown:', breakdown);
    console.groupEnd();
  }

  /**
   * Clear metrics
   */
  clear(): void {
    this.metrics = [];
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): QueryMetric[] {
    return [...this.metrics];
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitorService();

// Convenience function to measure query performance
export const measureQuery = async <T>(
  queryType: string,
  queryFunction: () => Promise<T>,
  options: {
    cached?: boolean;
    deduplicated?: boolean;
  } = {}
): Promise<T> => {
  const startTime = Date.now();
  
  try {
    const result = await queryFunction();
    const duration = Date.now() - startTime;
    
    performanceMonitor.recordQuery(queryType, duration, options);
    
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    
    performanceMonitor.recordQuery(queryType, duration, {
      ...options,
      error: error instanceof Error ? error.message : String(error)
    });
    
    throw error;
  }
};

declare global {
  interface Window {
    __BLISSNET_PERF_SUMMARY_INTERVAL__?: ReturnType<typeof setInterval>;
  }
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  if (!window.__BLISSNET_PERF_SUMMARY_INTERVAL__) {
    window.__BLISSNET_PERF_SUMMARY_INTERVAL__ = setInterval(() => {
      performanceMonitor.logPerformanceSummary();
    }, 30 * 60 * 1000);
  }

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      if (window.__BLISSNET_PERF_SUMMARY_INTERVAL__) {
        clearInterval(window.__BLISSNET_PERF_SUMMARY_INTERVAL__);
        delete window.__BLISSNET_PERF_SUMMARY_INTERVAL__;
      }
    });
  }
}

